import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MainCard from "components/MainCard";
import { OFFER_TYPES, getOfferType } from "./OfferList";
import { couponCodeChipSx, offerTypeChipSx } from "components/table/tableChipStyles";
import brand from "utils/designTokens";
import { useCurrency } from "contexts/CurrencyContext";

import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon      from "@mui/icons-material/ArrowForward";
import CheckCircleIcon       from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon      from "@mui/icons-material/InfoOutlined";
import LocalOfferIcon        from "@mui/icons-material/LocalOffer";
import AccessTimeIcon        from "@mui/icons-material/AccessTime";
import SettingsIcon          from "@mui/icons-material/Settings";
import TuneIcon              from "@mui/icons-material/Tune";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon             from "@mui/icons-material/Close";

import {
  Box, Grid, TextField, Button, Typography, Switch,
  FormControlLabel, CircularProgress, Alert, Stepper, Step, StepLabel,
  Paper, Chip, Divider, InputAdornment, Autocomplete, Checkbox,
  FormGroup, FormLabel, FormControl, ToggleButton, ToggleButtonGroup,
  Stack, Avatar, IconButton, LinearProgress
} from "@mui/material";

import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(customParseFormat);

const PAYMENT_OPTIONS = [
  { value: "cod",    label: "COD"        },
  { value: "online", label: "Online Pay" },
  { value: "wallet", label: "Wallet"     },
  { value: "card",   label: "Card"       },
];

const DURATION_PRESETS = [
  { value: "unlimited", label: "Unlimited", hours: null },
  { value: "24h",       label: "24 Hours",  hours: 24   },
  { value: "12h",       label: "12 Hours",  hours: 12   },
  { value: "custom",    label: "Custom",    hours: -1   },
];

const STEPS = [
  { label: "Offer Type",    icon: <LocalOfferIcon /> },
  { label: "Basic Details", icon: <InfoOutlinedIcon /> },
  { label: "Validity",      icon: <AccessTimeIcon /> },
  { label: "Conditions",    icon: <TuneIcon /> },
  { label: "Type Config",   icon: <SettingsIcon /> },
];

const offerTypeHints = {
  discount:    "Flat discount on cart total for all customers",
  coupon:      "Promo code that customers enter at checkout",
  category:    "Discount on items from selected categories",
  item:        "Discount on specific menu items",
  store:       "Exclusive offer for specific stores",
  customer:    "Targeted offer for selected customer segments",
  order_count: "Reward customers based on order history",
};

const emptyForm = () => ({
  offer_name_en:           "",
  offer_name_ar:           "",
  description:             "",
  offer_type:              "",
  offer_value_type:        "percentage",
  offer_value:             "",
  coupon_code:             "",
  from_datetime:           null,
  to_datetime:             null,
  minimum_purchase_amount: "",
  accepted_payments:       [],
  usage_limit_per_user:    "",
  total_usage_limit:       "",
  status:                  true,
  image_en:                null,
  image_ar:                null,
  image_en_url:            "",
  image_ar_url:            "",
  _duration_preset:        "custom",
  category_ids:            [],
  item_ids:                [],
  store_ids:               [],
  customer_ids:            [],
  min_order_count:         "",
  max_discount_amount:     "",
  auto_apply:              false,
  is_first_order:          false,
});

function ImageUploadBox({ label, fieldName, file, existingUrl, onFileChange, onClear }) {
  const preview = file ? URL.createObjectURL(file) : existingUrl || null;
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>
      <Box sx={{
        border: "2px dashed", borderColor: preview ? "primary.main" : "divider",
        borderRadius: 2, overflow: "hidden", position: "relative",
        bgcolor: preview ? "transparent" : "grey.50", minHeight: 130,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "border-color 0.2s", "&:hover": { borderColor: "primary.main" },
      }}>
        {preview ? (
          <>
            <img src={preview} alt={label} style={{ width: "100%", height: 130, objectFit: "cover", display: "block" }} />
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClear(); }}
              sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" }, width: 24, height: 24 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Button component="label" size="small" variant="contained"
              sx={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", fontSize: 11, py: 0.4, px: 1.5, whiteSpace: "nowrap" }}>
              Change Image
              <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && onFileChange(fieldName, e.target.files[0])} />
            </Button>
          </>
        ) : (
          <Button component="label" sx={{ flexDirection: "column", gap: 0.5, p: 3, width: "100%" }}>
            <AddPhotoAlternateIcon sx={{ fontSize: 34, color: "text.disabled" }} />
            <Typography variant="caption" color="text.secondary">Click to upload</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>JPG · PNG · WEBP · Max 2 MB</Typography>
            <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && onFileChange(fieldName, e.target.files[0])} />
          </Button>
        )}
      </Box>
    </Box>
  );
}

function StepOfferType({ form, setForm }) {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>Select Offer Type</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose the type of offer you want to create.
      </Typography>
      <Grid container spacing={2}>
        {OFFER_TYPES.map((t) => {
          const selected = form.offer_type === t.value;
          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.value}>
              <Paper elevation={0} onClick={() => setForm((p) => ({ ...p, offer_type: t.value }))}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "2px solid",
                  borderColor: selected ? brand.primary : "divider",
                  bgcolor: selected ? "rgba(31, 91, 64, 0.08)" : "background.paper",
                  "&:hover": {
                    borderColor: brand.primary,
                    bgcolor: "rgba(31, 91, 64, 0.06)"
                  },
                  position: "relative"
                }}>
                {selected && (
                  <CheckCircleIcon
                    sx={{ position: "absolute", top: 10, right: 10, color: brand.primary, fontSize: 20 }}
                  />
                )}
                <Typography variant="subtitle2" fontWeight={700} color={selected ? "primary.main" : "text.primary"}>
                  {t.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">{offerTypeHints[t.value]}</Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function StepBasicDetails({ form, setForm }) {
  const { currency } = useCurrency();
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };
  const isCoupon = form.offer_type === "coupon";

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Basic Information</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField fullWidth required label="Offer Name (English)" name="offer_name_en"
              value={form.offer_name_en} onChange={handleChange} placeholder="e.g. Weekend Special Discount" />
            <ImageUploadBox label="Offer Image (English)" fieldName="image_en"
              file={form.image_en} existingUrl={form.image_en_url}
              onFileChange={(f, file) => setForm((p) => ({ ...p, [f]: file }))}
              onClear={() => setForm((p) => ({ ...p, image_en: null, image_en_url: "" }))} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField fullWidth label="Offer Name (Arabic)" name="offer_name_ar"
              value={form.offer_name_ar} onChange={handleChange}
              inputProps={{ dir: "rtl" }} placeholder="اسم العرض بالعربية" />
            <ImageUploadBox label="Offer Image (Arabic)" fieldName="image_ar"
              file={form.image_ar} existingUrl={form.image_ar_url}
              onFileChange={(f, file) => setForm((p) => ({ ...p, [f]: file }))}
              onClear={() => setForm((p) => ({ ...p, image_ar: null, image_ar_url: "" }))} />
          </Stack>
        </Grid>
        <Grid size={12}>
          <TextField fullWidth multiline rows={3} label="Description" name="description"
            value={form.description} onChange={handleChange}
            placeholder="Describe the offer details visible to customers…" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Offer Value Type</FormLabel>
            <ToggleButtonGroup exclusive value={form.offer_value_type}
              onChange={(_, v) => v && setForm((p) => ({ ...p, offer_value_type: v }))}
              fullWidth size="small" sx={{ height: 40 }}>
              <ToggleButton value="percentage">% Percentage</ToggleButton>
              <ToggleButton value="fixed">{currency.symbol} Fixed Amount</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Offer Value</FormLabel>
            <TextField fullWidth size="small" type="number" name="offer_value"
              value={form.offer_value} onChange={handleChange}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
              InputProps={{ endAdornment: <InputAdornment position="end">{form.offer_value_type === "percentage" ? "%" : currency.symbol}</InputAdornment> }} />
          </FormControl>
        </Grid>

        {form.offer_value_type === "percentage" && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth type="number" label="Max Discount Amount (Cap)"
              name="max_discount_amount" value={form.max_discount_amount} onChange={handleChange}
              placeholder={`Optional: maximum ${currency.symbol} discount`}
              InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
              helperText="Leave empty for no cap" />
          </Grid>
        )}

        {isCoupon && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth required label="Coupon Code" name="coupon_code"
              value={form.coupon_code}
              onChange={(e) => setForm((p) => ({ ...p, coupon_code: e.target.value.toUpperCase() }))}
              placeholder="e.g. SAVE20"
              inputProps={{ style: { fontFamily: "monospace", fontWeight: 700, letterSpacing: 2 } }}
              helperText="Alphanumeric, auto-uppercased" />
          </Grid>
        )}

        {!isCoupon && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Switch checked={form.auto_apply} onChange={(e) => setForm((p) => ({ ...p, auto_apply: e.target.checked }))} />}
              label={<Box><Typography variant="body2" fontWeight={600}>Auto Apply</Typography>
                <Typography variant="caption" color="text.secondary">Automatically applied without customer action</Typography></Box>} />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControlLabel
            control={<Switch checked={form.is_first_order} onChange={(e) => setForm((p) => ({ ...p, is_first_order: e.target.checked }))} />}
            label={<Box><Typography variant="body2" fontWeight={600}>First Order Only</Typography>
              <Typography variant="caption" color="text.secondary">Valid only for customer's first order</Typography></Box>} />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepValidity({ form, setForm }) {
  const preset = form._duration_preset || "custom";

  const applyPreset = (nextPreset, fromDt) => {
    const from = fromDt ?? form.from_datetime;
    setForm((p) => {
      const next = { ...p, _duration_preset: nextPreset };
      if (!from) return next;
      if (nextPreset === "unlimited") next.to_datetime = dayjs(from).add(10, "year");
      else if (nextPreset === "24h")  next.to_datetime = dayjs(from).add(24, "hour");
      else if (nextPreset === "12h")  next.to_datetime = dayjs(from).add(12, "hour");
      return next;
    });
  };

  const handleFromChange = (val) => {
    setForm((p) => ({ ...p, from_datetime: val }));
    if (preset && preset !== "custom") applyPreset(preset, val);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Offer Validity Period</Typography>
      <Grid container spacing={3}>
        <Grid size={12}>
          <FormControl>
            <FormLabel sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>Duration Preset</FormLabel>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {DURATION_PRESETS.map((d) => {
                const active = preset === d.value;
                return (
                  <Paper key={d.value} elevation={0} onClick={() => applyPreset(d.value, null)}
                    sx={{ px: 2.5, py: 1.2, borderRadius: 2, cursor: "pointer", border: "2px solid",
                      borderColor: active ? "primary.main" : "divider",
                      bgcolor: active ? "primary.lighter" : "background.paper",
                      minWidth: 90, textAlign: "center" }}>
                    <Typography variant="body2" fontWeight={active ? 700 : 500} color={active ? "primary.main" : "text.secondary"}>
                      {d.label}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker label="Start Date & Time " value={form.from_datetime} onChange={handleFromChange}
            slotProps={{ textField: { fullWidth: true, required: true } }} disablePast />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker label="End Date & Time " value={form.to_datetime}
            onChange={(v) => setForm((p) => ({ ...p, to_datetime: v, _duration_preset: "custom" }))}
            slotProps={{ textField: { fullWidth: true, required: true } }}
            minDateTime={form.from_datetime || undefined}
            disabled={preset === "unlimited"} />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Usage Limits</Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Usage Limit Per User"
            name="usage_limit_per_user" value={form.usage_limit_per_user}
            onChange={(e) => setForm((p) => ({ ...p, usage_limit_per_user: e.target.value }))}
            placeholder="e.g. 1 (leave empty = unlimited)" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Total Usage Limit"
            name="total_usage_limit" value={form.total_usage_limit}
            onChange={(e) => setForm((p) => ({ ...p, total_usage_limit: e.target.value }))}
            placeholder="e.g. 500 (leave empty = unlimited)" />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepConditions({ form, setForm }) {
  const { currency } = useCurrency();
  const togglePayment = (val) => {
    setForm((p) => ({
      ...p,
      accepted_payments: p.accepted_payments.includes(val)
        ? p.accepted_payments.filter((v) => v !== val)
        : [...p.accepted_payments, val],
    }));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Purchase Conditions</Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField fullWidth type="number" label="Minimum Purchase Amount"
            name="minimum_purchase_amount" value={form.minimum_purchase_amount}
            onChange={(e) => setForm((p) => ({ ...p, minimum_purchase_amount: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency.symbol}</InputAdornment> }}
            helperText="Minimum cart value required" />
        </Grid>

        <Grid size={12}>
          <FormControl component="fieldset">
            <FormLabel sx={{ fontWeight: 600, mb: 1.5 }}>Accepted Payment Methods</FormLabel>
            <FormGroup row>
              {PAYMENT_OPTIONS.map((p) => (
                <FormControlLabel key={p.value}
                  control={<Checkbox checked={form.accepted_payments.includes(p.value)} onChange={() => togglePayment(p.value)} />}
                  label={p.label} sx={{ mr: 3 }} />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={<Switch checked={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked }))} />}
            label="Activate Immediately" />
        </Grid>
      </Grid>
    </Box>
  );
}

function StepTypeConfig({ form, setForm }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingDeps, setLoadingDeps] = useState(false);

  useEffect(() => {
    const fetchDeps = async () => {
      if (!form.offer_type) return;
      setLoadingDeps(true);
      try {
        if (form.offer_type === "category") {
          const r = await api.get("/category/list");
          setCategories(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
        } else if (form.offer_type === "item") {
          const r = await api.get("/item/list");
          setItems(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
        } else if (form.offer_type === "store") {
          const r = await api.get("/store/list");
          setStores(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
        } else if (form.offer_type === "customer") {
          const r = await api.get("/mobile/customer/list");
          setCustomers(Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : []);
        }
      } catch (e) {
        console.error("Failed to fetch dependencies", e);
      } finally {
        setLoadingDeps(false);
      }
    };
    fetchDeps();
  }, [form.offer_type]);

  if (loadingDeps) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {getOfferType(form.offer_type).label} Configuration
      </Typography>
      <Grid container spacing={3}>
        {form.offer_type === "discount" && (
          <Grid size={12}>
            <Paper sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography color="text.secondary">This offer applies a flat discount to the entire cart. No additional configuration needed.</Typography>
            </Paper>
          </Grid>
        )}

        {form.offer_type === "coupon" && (
          <Grid size={12}>
            <Paper sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography color="text.secondary">Customers will enter code: <strong style={{ fontFamily: "monospace" }}>{form.coupon_code || "—"}</strong> at checkout.</Typography>
            </Paper>
          </Grid>
        )}

        {form.offer_type === "category" && (
          <Grid size={12}>
            <Autocomplete multiple options={categories}
              getOptionLabel={(o) => o.category_name_en || o.name || ""}
              value={categories.filter(c => form.category_ids.includes(c.category_id))}
              onChange={(_, v) => setForm(p => ({ ...p, category_ids: v.map(c => c.category_id) }))}
              renderInput={(params) => <TextField {...params} label="Select Categories *" placeholder="Search categories..." />}
              renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option.category_name_en || option.name} {...getTagProps({ index })} size="small" />))}
            />
          </Grid>
        )}

        {form.offer_type === "item" && (
          <Grid size={12}>
            <Autocomplete multiple options={items}
              getOptionLabel={(o) => o.item_name_en || o.name || ""}
              value={items.filter(i => form.item_ids.includes(i.item_id))}
              onChange={(_, v) => setForm(p => ({ ...p, item_ids: v.map(i => i.item_id) }))}
              renderInput={(params) => <TextField {...params} label="Select Items *" placeholder="Search items..." />}
              renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option.item_name_en || option.name} {...getTagProps({ index })} size="small" />))}
            />
          </Grid>
        )}

        {form.offer_type === "store" && (
          <Grid size={12}>
            <Autocomplete multiple options={stores}
              getOptionLabel={(o) => o.title_en || o.name || ""}
              value={stores.filter(s => form.store_ids.includes(s.store_id))}
              onChange={(_, v) => setForm(p => ({ ...p, store_ids: v.map(s => s.store_id) }))}
              renderInput={(params) => <TextField {...params} label="Select Stores *" placeholder="Search stores..." />}
              renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option.title_en || option.name} {...getTagProps({ index })} size="small" />))}
            />
          </Grid>
        )}

        {form.offer_type === "customer" && (
          <Grid size={12}>
            <Autocomplete multiple options={customers}
              getOptionLabel={(o) => `${o.name || o.full_name || ""} (${o.mobile || o.phone || ""})`}
              value={customers.filter(c => form.customer_ids.includes(c.customer_id || c.id))}
              onChange={(_, v) => setForm(p => ({ ...p, customer_ids: v.map(c => c.customer_id || c.id) }))}
              renderInput={(params) => <TextField {...params} label="Select Customers *" placeholder="Search customers..." />}
              renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option.name || option.full_name} {...getTagProps({ index })} size="small" />))}
            />
          </Grid>
        )}

        {form.offer_type === "order_count" && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth required type="number" label="Minimum Order Count"
              name="min_order_count" value={form.min_order_count}
              onChange={(e) => setForm(p => ({ ...p, min_order_count: e.target.value }))}
              helperText="Customer must have completed at least this many orders" />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function OfferSummary({ form }) {
  const { currency } = useCurrency();
  const ot = getOfferType(form.offer_type);
  const previewImg = form.image_en ? URL.createObjectURL(form.image_en) : form.image_en_url || null;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Offer Preview</Typography>
      <Divider sx={{ mb: 2 }} />
      {previewImg && (
        <Box sx={{ mb: 2, borderRadius: 2, overflow: "hidden", height: 120, bgcolor: "grey.100" }}>
          <img src={previewImg} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </Box>
      )}
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip label={ot.label} size="small" variant="outlined" sx={offerTypeChipSx} />
          {form.coupon_code && (
            <Chip label={form.coupon_code} size="small" variant="outlined" sx={couponCodeChipSx} />
          )}
        </Box>
        {form.offer_name_en && <Typography variant="body1" fontWeight={700}>{form.offer_name_en}</Typography>}
        {form.offer_value && (
          <Typography variant="h5" sx={{ color: brand.primary }} fontWeight={800}>
            {form.offer_value_type === "percentage" ? `${form.offer_value}% OFF` : `${currency.symbol} ${form.offer_value} OFF`}
          </Typography>
        )}
        {(form.from_datetime || form.to_datetime) && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Validity:</Typography>
            <Typography variant="caption" fontWeight={600}>
              {form.from_datetime ? dayjs(form.from_datetime).format("DD MMM YY") : "—"} →{" "}
              {form._duration_preset === "unlimited" ? "No expiry" : (form.to_datetime ? dayjs(form.to_datetime).format("DD MMM YY") : "—")}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export default function OfferForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!isEdit) return;
    setPageLoading(true);
    api.get(`/offer/list/${id}`)
      .then((res) => {
        const d = res.data?.data || res.data;
        if (!d) {
          setError("Offer not found");
          return;
        }
        const parseArray = (val) => {
          if (Array.isArray(val)) return val;
          if (typeof val === 'string') {
            try { return JSON.parse(val); } catch (e) { return []; }
          }
          return [];
        };

        setForm({
          offer_name_en:           d.offer_name_en || "",
          offer_name_ar:           d.offer_name_ar || "",
          description:             d.description || "",
          offer_type:              d.offer_type || "",
          offer_value_type:        d.offer_value_type || "percentage",
          offer_value:             d.offer_value ?? "",
          coupon_code:             d.coupon_code || "",
          from_datetime:           d.from_datetime ? dayjs(d.from_datetime) : null,
          to_datetime:             d.to_datetime ? dayjs(d.to_datetime) : null,
          minimum_purchase_amount: d.minimum_purchase_amount ?? "",
          accepted_payments:       parseArray(d.accepted_payments),
          usage_limit_per_user:    d.usage_limit_per_user ?? "",
          total_usage_limit:       d.total_usage_limit ?? "",
          status:                  d.status !== undefined ? Boolean(Number(d.status)) : true,
          image_en:                null,
          image_ar:                null,
          image_en_url:            d.image_en || "",
          image_ar_url:            d.image_ar || "",
          _duration_preset:        "custom",
          category_ids:            parseArray(d.category_ids),
          item_ids:                parseArray(d.item_ids),
          store_ids:               parseArray(d.store_ids),
          customer_ids:            parseArray(d.customer_ids),
          min_order_count:         d.min_order_count ?? "",
          max_discount_amount:     d.max_discount_amount ?? "",
          auto_apply:              Boolean(Number(d.auto_apply)),
          is_first_order:          Boolean(Number(d.is_first_order)),
        });
      })
      .catch(() => setError("Failed to load offer data"))
      .finally(() => setPageLoading(false));
  }, [id, isEdit]);

  const validateStep = useCallback((step) => {
    switch (step) {
      case 0: return !!form.offer_type;
      case 1: 
        if (!form.offer_name_en.trim() || !form.offer_value) return false;
        if (form.offer_type === "coupon" && !form.coupon_code.trim()) return false;
        return true;
      case 2: return !!(form.from_datetime && form.to_datetime && dayjs(form.to_datetime).isAfter(form.from_datetime));
      case 4:
        if (form.offer_type === "category" && !form.category_ids.length) return false;
        if (form.offer_type === "item" && !form.item_ids.length) return false;
        if (form.offer_type === "store" && !form.store_ids.length) return false;
        if (form.offer_type === "customer" && !form.customer_ids.length) return false;
        if (form.offer_type === "order_count" && !form.min_order_count) return false;
        return true;
      default: return true;
    }
  }, [form]);

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      setError("Please complete all required fields in this step.");
      return;
    }
    setError("");
    setActiveStep(s => s + 1);
  };

  const handleBack = () => {
    setError("");
    setActiveStep(s => s - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      setError("Please complete the type-specific configuration.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const fd = new FormData();
      
      const payload = {
        offer_name_en:           form.offer_name_en,
        offer_name_ar:           form.offer_name_ar,
        description:             form.description,
        offer_type:              form.offer_type,
        offer_value_type:        form.offer_value_type,
        offer_value:             parseFloat(form.offer_value) || 0,
        coupon_code:             form.coupon_code ? form.coupon_code.toUpperCase() : null,
        from_datetime:           form.from_datetime ? dayjs(form.from_datetime).toISOString() : null,
        to_datetime:             form.to_datetime ? dayjs(form.to_datetime).toISOString() : null,
        minimum_purchase_amount: form.minimum_purchase_amount ? parseFloat(form.minimum_purchase_amount) : null,
        max_discount_amount:     form.max_discount_amount ? parseFloat(form.max_discount_amount) : null,
        usage_limit_per_user:    form.usage_limit_per_user ? parseInt(form.usage_limit_per_user) : null,
        total_usage_limit:       form.total_usage_limit ? parseInt(form.total_usage_limit) : null,
        min_order_count:         form.min_order_count ? parseInt(form.min_order_count) : null,
        accepted_payments:       form.accepted_payments,
        category_ids:            form.category_ids,
        item_ids:                form.item_ids,
        store_ids:               form.store_ids,
        customer_ids:            form.customer_ids,
        auto_apply:              form.auto_apply ? 1 : 0,
        is_first_order:          form.is_first_order ? 1 : 0,
        status:                  form.status ? 1 : 0,
      };

      Object.entries(payload).forEach(([k, v]) => {
        if (v !== null && v !== undefined) {
          if (Array.isArray(v)) {
            fd.append(k, JSON.stringify(v));
          } else {
            fd.append(k, v);
          }
        }
      });

      if (form.image_en instanceof File) {
        fd.append("image_en", form.image_en);
      }
      if (form.image_ar instanceof File) {
        fd.append("image_ar", form.image_ar);
      }

      const hasFiles = (form.image_en instanceof File) || (form.image_ar instanceof File);

      let response;
      if (hasFiles) {
     
        if (isEdit) {
          response = await api.put(`/offers/edit/${id}`, fd);
        } else {
          response = await api.post("/offers/create", fd);
        }
      } else {
        const jsonPayload = {
          ...payload,
   
        };
        
        if (isEdit) {
          response = await api.put(`/offers/edit/${id}`, jsonPayload);
        } else {
          response = await api.post("/offers/create", jsonPayload);
        }
      }
      
      navigate("/offer/list");
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.message || "Save failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 12, gap: 2 }}>
      <CircularProgress size={48} />
      <Typography variant="body2" color="text.secondary">Loading offer details...</Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <MainCard
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalOfferIcon color="primary" />
                  <Typography variant="h6">{isEdit ? "Edit Offer" : "Create New Offer"}</Typography>
                </Box>
              }
              contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
            >
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {STEPS.map((s, i) => (
                  <Step key={i} completed={i < activeStep}>
                    <StepLabel StepIconComponent={({ active, completed }) => (
                      <Avatar sx={{ width: 32, height: 32,
                        bgcolor: completed ? "success.main" : active ? "primary.main" : "grey.200",
                        color:   completed || active ? "#fff" : "text.secondary", fontSize: 14 }}>
                        {completed ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : i + 1}
                      </Avatar>
                    )}>
                      <Typography variant="caption" fontWeight={activeStep === i ? 700 : 400}>{s.label}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ minHeight: 400 }}>
                {[
                  <StepOfferType key={0} form={form} setForm={setForm} />,
                  <StepBasicDetails key={1} form={form} setForm={setForm} />,
                  <StepValidity key={2} form={form} setForm={setForm} />,
                  <StepConditions key={3} form={form} setForm={setForm} />,
                  <StepTypeConfig key={4} form={form} setForm={setForm} />
                ][activeStep]}
              </Box>

              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} 
                  onClick={activeStep === 0 ? () => navigate("/offer/list") : handleBack}
                  disabled={submitting}>
                  {activeStep === 0 ? "Cancel" : "Back"}
                </Button>
                {activeStep < 4 ? (
                  <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleNext} disabled={!validateStep(activeStep)}>
                    Next
                  </Button>
                ) : (
                  <Button variant="contained" color="success" onClick={handleSubmit} disabled={submitting || !validateStep(4)}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}>
                    {submitting ? "Saving..." : isEdit ? "Update Offer" : "Create Offer"}
                  </Button>
                )}
              </Box>
            </MainCard>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2} sx={{ position: "sticky", top: 20 }}>
              {form.offer_type && <OfferSummary form={form} />}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}